import {
  UserContextRootState,
  selectDoesUserHaveRbacPermission,
  selectFeatureFlags,
  selectIsBossOrSalesBossLevel,
} from 'core/userContext';
import { useJourneyAssistContext } from 'modules/workflows/context/JourneyAssistContext';
import {
  getIsJourneyAIGenerationEnabled,
  getIsWorkflowSaving,
  getWorkflowType,
  openModal,
} from 'modules/workflows/ducks';
import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterState } from 'redux-first-history';
import styled from 'styled-components';
import { BUTTON_VARIANTS, Button } from 'ui/button';
import { ALIGNMENT, Menu } from 'ui/menu';
import { ChevronUp } from 'ui/menu/Styled';
import { colors } from 'ui/theme';
import { ItemData, useFlowChart } from 'ui/workflows/flowchart';
import noop from 'utils/noop';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { DELETE_DRAFT_MODAL, NODE_TYPES } from '../../../constants';
import { Edge, Node, Workflow } from '../../../types';
import { Actions, FooterBar } from '../Footer.styles';
import { CloningCallbackProps, DeletionCallbackProps, NodeSelectionBar } from '../NodeSelectionBar';
import { DraftActions } from './DraftActions';
import { ExitCriteriaButton } from './ExitCriteriaButton';
import { PublishedActions } from './PublishedActions';
import { MENU_OPTION } from './constants';
import { SelectedEdge, filterSelectedEdge } from './utils';

interface ActionBarProps {
  /** Is the workflow enabled? */
  isWorkflowEnabled: boolean;
  /** Callback when "Fix stuck user" is clicked */
  onFixStuckUsersClick?: () => void;
  /** Callback when "Live" toggle is clicked */
  onLiveToggleClick?: () => void;
  /** Callback when "Clone selected" is clicked */
  onOpenCloneNodeModal: (modalProps: CloningCallbackProps) => void;
  /** Callback when "Delete selected" is clicked */
  onOpenDeleteNodeModal: (modalProps: DeletionCallbackProps) => void;
  /** Callback when "Create draft" is clicked */
  onCreateDraft: () => void;
  /** Callback when "Send Test" is clicked */
  onSendTestTrigger: () => void;
  /** Callback when "Publish workflow" is clicked for new draft workflows */
  onPublishNewWorkflow: () => void;
  /** Callback when "Publish workflow" is clicked for existing workflow */
  onPublishExistingWorkflow: () => void;
  /** Callback when "Save Workflow" is clicked */
  onSaveDraft?: () => void;
  /** Callback when "Reset to published" is clicked */
  onResetDraft?: () => void;
  onExitRulesClick?: () => void;
  isNodeEditorOpen?: boolean;
  nodes?: Node[];
  workflow: Pick<Workflow, 'id' | 'workflowReference' | 'workflowType' | 'startWorkflowNodeId'>;
  updatedAt: string;
}

export const ACTION_BAR_TEST_ID = 'action-bar';
export const MANAGE_MENU_TEST_ID = 'action-bar-manage-menu';

const ChevronUpRightSide = styled(ChevronUp)`
  && {
    path {
      stroke: ${colors.grey80};
    }
  }
`;

const StyledMenu = styled(Menu)`
  min-width: 216px;
`;

export const getMenuOptions = (
  hasDraftPermissions = false,
  isPublished = false,
  hasBossPermissions = false,
) => {
  const baseMenuOption = [MENU_OPTION.SAVE_AS_IMAGE];

  if (isPublished && hasBossPermissions) {
    return [MENU_OPTION.FIX_STUCK_USERS, MENU_OPTION.DIVIDER, ...baseMenuOption];
  }

  if (!hasDraftPermissions) {
    return baseMenuOption;
  }

  return isPublished
    ? baseMenuOption
    : [
        MENU_OPTION.DELETE_DRAFT,
        MENU_OPTION.RESET_TO_PUBLISHED,
        MENU_OPTION.DIVIDER,
        ...baseMenuOption,
      ];
};

type RBACSelectorType = UserContextRootState & { router: RouterState };

export const ActionBar: FC<ActionBarProps> = ({
  isNodeEditorOpen = false,
  isWorkflowEnabled = false,
  onCreateDraft = noop,
  onFixStuckUsersClick = noop,
  onLiveToggleClick = noop,
  onOpenCloneNodeModal = noop,
  onOpenDeleteNodeModal = noop,
  onSaveDraft = noop,
  onSendTestTrigger = noop,
  nodes = [],
  onPublishNewWorkflow = noop,
  onPublishExistingWorkflow = noop,
  onResetDraft,
  updatedAt,
  workflow,
}): JSX.Element => {
  const dispatch = useDispatch();

  const { downloadFullImage, getNodeEdges } = useFlowChart();
  const { workflowReference } = workflow;

  const { isPublished, isDraft } = useSelector(getWorkflowType);
  const canDraftMessaging = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state as RBACSelectorType)(PROJECT_PERMISSIONS.DraftMessaging),
  );
  const canManageMessaging = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state as RBACSelectorType)(
      PROJECT_PERMISSIONS.ManageMessaging,
    ),
  );

  const isBaus = useSelector(selectIsBossOrSalesBossLevel);
  const { FixStuckWorkflowUsers } = useSelector(selectFeatureFlags);

  const isSaving = useSelector(getIsWorkflowSaving);
  const isJourneyAIGenerationEnabled = useSelector(getIsJourneyAIGenerationEnabled);

  const { isJourneyAssistActive } = useJourneyAssistContext();

  const isStartNodeSelected = nodes.some(
    (node) => node.type === NODE_TYPES.START && node.isSelected === true,
  );

  const { nodes: selectedNodes, edges: selectedEdges } = useMemo(() => {
    // filter down to nodes that are selected and can be cloned and/or deleted
    const selNodes = nodes.filter(
      (node) => node.isSelected === true && (node.canClone === true || node.canDelete === true),
    );

    // filter edges to only include those that connect the selected nodes above
    const selEdges =
      selNodes.length <= 1
        ? []
        : selNodes.reduce((acc: ItemData[], currNode: Node) => {
            return [
              ...acc,
              ...getNodeEdges(currNode?.id).filter((currEdge: ItemData) =>
                filterSelectedEdge({ currEdge: currEdge as SelectedEdge, currNode, selNodes }),
              ),
            ];
          }, []);

    return {
      nodes: selNodes,
      edges: selEdges,
    };
  }, [nodes]);

  const shouldShowSelectionBar = selectedNodes.length > 0 && !isNodeEditorOpen;

  const handleSelectionChange = (selection: keyof typeof MENU_OPTION) =>
    ({
      [MENU_OPTION.SAVE_AS_IMAGE.id]: () => downloadFullImage(),
      [MENU_OPTION.DELETE_DRAFT.id]: () => dispatch(openModal(DELETE_DRAFT_MODAL, {})),
      [MENU_OPTION.RESET_TO_PUBLISHED.id]: () => onResetDraft?.(),
      [MENU_OPTION.FIX_STUCK_USERS.id]: () => onFixStuckUsersClick(),
    })[selection]();

  if (isJourneyAIGenerationEnabled && isJourneyAssistActive) return <></>;

  return shouldShowSelectionBar ? (
    <NodeSelectionBar
      canDraftMessaging={canDraftMessaging}
      isStartNodeSelected={isStartNodeSelected}
      onOpenCloneNodeModal={onOpenCloneNodeModal}
      onOpenDeleteNodeModal={onOpenDeleteNodeModal}
      selectedNodes={selectedNodes}
      selectedEdges={selectedEdges as unknown as Edge[]}
      isPublishedSelectionBar={isPublished}
    />
  ) : (
    <FooterBar data-test={ACTION_BAR_TEST_ID} data-testid={ACTION_BAR_TEST_ID}>
      <Actions>
        {isDraft && <ExitCriteriaButton />}
        <StyledMenu
          horizontalAlignment={ALIGNMENT.LEFT}
          verticalAlignment={ALIGNMENT.UP}
          options={getMenuOptions(canDraftMessaging, isPublished, isBaus && FixStuckWorkflowUsers)}
          id={MANAGE_MENU_TEST_ID}
          handleSelectionChange={handleSelectionChange}
          data-test={MANAGE_MENU_TEST_ID}
        >
          <Button variant={BUTTON_VARIANTS.SECONDARY}>
            <ChevronUpRightSide height={16} width={16} />
          </Button>
        </StyledMenu>
      </Actions>
      {isPublished ? (
        <PublishedActions
          canDraftMessaging={canDraftMessaging}
          canManageMessaging={canManageMessaging}
          isWorkflowEnabled={isWorkflowEnabled}
          onLiveToggleClick={onLiveToggleClick}
          workflow={workflow}
          onCreateDraft={onCreateDraft}
          updatedAt={updatedAt}
        />
      ) : (
        <DraftActions
          canDraftMessaging={canDraftMessaging}
          canManageMessaging={canManageMessaging}
          hasPublished={!!workflowReference}
          isSaving={isSaving}
          onSendTestTrigger={onSendTestTrigger}
          onPublishNewWorkflow={onPublishNewWorkflow}
          onPublishExistingWorkflow={onPublishExistingWorkflow}
          onSaveDraft={onSaveDraft}
          updatedAt={updatedAt}
        />
      )}
    </FooterBar>
  );
};
